<template>
  <div class="container-fluid py-4 mt-5 pt-5">
    <TableBreadCrumbs title="CardTypes" />
    <!-- Header with Tabs and Create Button -->
    <div class="d-flex justify-content-between align-items-center mb-4">
      <!-- Tabs -->
      <ul class="nav nav-tabs" role="tablist">
        <li class="nav-item" role="presentation">
          <button
            class="nav-link"
            :class="{ active: activeTab === 'default' }"
            @click="setActiveTab('default')"
            type="button"
          >
            Default
          </button>
        </li>
        <li class="nav-item" role="presentation">
          <button
            class="nav-link"
            :class="{ active: activeTab === 'byusers' }"
            @click="setActiveTab('byusers')"
            type="button"
          >
            By Users
          </button>
        </li>
      </ul>

      <!-- Create Button - Only show for Default tab -->
      <button
        v-if="activeTab === 'default'"
        class="btn btn-primary"
        @click="openCreateModal"
        :disabled="!canCreate('main')"
      >
        <i class="fa fa-plus me-2"></i>
        Create New Card Type
      </button>
    </div>

    <!-- Tab Content -->
    <div class="card">
      <div class="card-header">
        <h5 class="card-title mb-0">
          {{ currentTabTitle }}
        </h5>
      </div>

      <div class="card-body">
        <!-- Loading State -->
        <div v-if="isLoading" class="text-center py-5">
          <div class="spinner-border text-primary" role="status">
            <span class="visually-hidden">Loading...</span>
          </div>
          <p class="mt-2">
            Loading {{ activeTab === "default" ? "Default" : "By Users" }} card
            types...
          </p>
        </div>

        <!-- Error State -->
        <div v-else-if="error" class="alert alert-danger">
          <div class="d-flex align-items-center">
            <i class="fa fa-exclamation-triangle me-2"></i>
            <div class="flex-grow-1"><strong>Error:</strong> {{ error }}</div>
            <button
              class="btn btn-sm btn-outline-danger"
              @click="refreshTable"
            >
              <i class="fa fa-refresh"></i> Retry
            </button>
          </div>
        </div>

        <!-- Card Types Table -->
        <div v-else-if="tableConfig.data?.length">
          <CommonTable2
            :tableObj="tableConfig"
            :title="''"
            permissionKey="main"
            @edit="handleEdit"
            @delete="handleDelete"
            @view="handleView"
          />
        </div>

        <!-- Empty State -->
        <div v-else class="text-center py-5">
          <div class="mb-3">
            <i class="fa fa-credit-card fa-3x text-muted"></i>
          </div>
          <h5 class="text-muted">
            No {{ activeTab === "default" ? "Default" : "By Users" }} Card Types
            Found
          </h5>
          <p class="text-muted">
            {{
              activeTab === "default"
                ? "No default card types are available."
                : "No By Users card types are available."
            }}
          </p>
          <div class="mt-3">
            <button
              v-if="activeTab === 'default'"
              class="btn btn-primary me-2"
              @click="openCreateModal"
              :disabled="!canCreate('main')"
            >
              <i class="fa fa-plus me-1"></i>
              Create Card Type
            </button>
            <button class="btn btn-outline-secondary" @click="refreshTable">
              <i class="fa fa-refresh me-1"></i>
              Refresh
            </button>
          </div>
        </div>
      </div>

      <!-- pagination -->
      <div
        class="d-flex justify-content-end align-items-center gap-3 mt-3 mb-4"
      >
        <!-- Rows per page -->
        <div class="d-flex align-items-center gap-2">
          <span class="text-muted">Rows per page:</span>
          <select
            class="form-select form-select-sm rows-select"
            v-model="tablePagination.per_page"
            @change="changeRows"
          >
            <option v-for="n in [5, 10, 20, 50, 100]" :key="n" :value="n">
              {{ n }}
            </option>
          </select>
        </div>
        <!-- Pagination Buttons -->
        <div class="pagination-mini">
          <!-- Previous -->
          <button
            class="page-btn prev_btn"
            :disabled="!tablePagination.has_prev"
            @click="handlePageChange(tablePagination.current_page - 1)"
          >
            «
          </button>
          <!-- Numbered Pages -->
          <button
            v-for="(p, index) in visiblePages"
            :key="index"
            class="page-btn"
            :class="{
              'page-active': p === tablePagination.current_page,
              'disabled-dots': p === '...',
            }"
            :disabled="p === '...'"
            @click="p !== '...' && handlePageChange(p)"
          >
            {{ p }}
          </button>

          <!-- Next -->
          <button
            class="page-btn next_btn"
            :disabled="!tablePagination.has_next"
            @click="handlePageChange(tablePagination.current_page + 1)"
          >
            »
          </button>
        </div>
      </div>

      <div v-if="tableConfig.data?.length" class="card-footer bg-light">
        <div class="d-flex justify-content-between align-items-center">
          <small class="text-muted">
            Showing {{ tableConfig.data.length }}
            {{ activeTab === "default" ? "default" : "By Users" }} card type
            {{ tableConfig.data.length !== 1 ? "s" : "" }}
          </small>
          <button
            class="btn btn-sm btn-outline-secondary"
            @click="refreshTable"
          >
            <i class="fa fa-refresh me-1"></i>
            Refresh
          </button>
        </div>
      </div>
    </div>
  </div>

  <!-- Create Modal -->
  <BaseModal
    id="createNew"
    :title="`Create ${pageTitle}`"
    :visible="isCreateModalOpen"
    @close="closeCreateModal"
    @save="handleSaveCreate"
    :save-button-disabled="isCreateFormValid"
  >
    <template #body>
      <BaseForm
        :fields="createFormFields"
        v-model="newItemData"
        ref="createFormRef"
      />

      <!-- 👇 Custom Icon Picker -->
      <div class="mb-3">
        <label class="form-label">Icon</label>
        <IconPicker v-model="newItemData.icon" />
      </div>
    </template>
  </BaseModal>

  <!-- Edit/View Modal -->
  <BaseModal
    id="editModal"
    :title="`${isViewMode ? 'View' : 'Edit'} ${pageTitle}`"
    :visible="isEditModalOpen"
    @close="closeEditModal"
    @save="handleSaveEdit"
    :show-save-button="!isViewMode"
    :save-button-disabled="isEditFormValid"
  >
    <template #body>
      <BaseForm
        :fields="editFormFields"
        v-model="editingItemData"
        ref="editFormRef"
      />

      <!-- 👇 Custom Icon Picker -->
      <div class="mb-3">
        <label class="form-label">Icon</label>
        <IconPicker v-model="editingItemData.icon" />
      </div>
    </template>
  </BaseModal>
</template>

<script lang="ts" setup>
import { ref, computed, onMounted, watch, defineAsyncComponent } from "vue";
import { useCrud } from "@/composables/commen/useCrud";
import BaseModal from "@/components/commen/modal/BaseModal.vue";
import BaseForm from "@/components/commen/modal/BaseForm.vue"; 
import CommonTable2 from "@/components/commen/table/CommonTable2.vue";
import TableBreadCrumbs from "@/components/commen/table/TableBreadCrumbs.vue";
import { usePermissions } from "@/composables/usePermissions";

const { canCreate } = usePermissions();

// Define IconUploader as async component
const IconPicker = defineAsyncComponent({
  loader: () => import("@/components/commen/modal/IconUploader.vue"),
  loadingComponent: {
    template: `
      <div class="text-center py-3">
        <div class="spinner-border spinner-border-sm" role="status"></div>
        <span class="ms-2">Loading icon picker...</span>
      </div>
    `,
  },
  errorComponent: {
    template: `
      <div class="alert alert-warning">
        <small>Icon picker unavailable. Using manual selection.</small>
      </div>
    `,
  },
  delay: 200,
  timeout: 3000,
});

const API_RESOURCE = "/common/cardtypes";
const pageTitle = "Card Types";
const title = "Card Types List";
const crud = useCrud(API_RESOURCE);
const getData = useCrud("/common/cardtypes-filtered");
const rows = ref([]);


// Tab state
const activeTab = ref<"default" | "byusers">("default");
const isLoading = ref(false);
const error = ref<string | null>(null);

// Computed properties
const currentTabTitle = computed(() =>
  activeTab.value === "default" ? "Default Card Types" : "By Users Card Types"
);

// pagination local UI state (mirrors backend, but we keep per_page from UI)
const tablePagination = ref({
  current_page: 1,
  per_page: 10,
  total: 0,
  total_pages: 0,
  has_next: false,
  has_prev: false,
});
const currentPage = ref(1);
// visiblePages computed (same advanced logic)
const visiblePages = computed(() => {
  const total = tablePagination.value.total_pages;
  const current = tablePagination.value.current_page;

  if (!total || total <= 4) {
    return Array.from({ length: Math.max(1, total) }, (_, i) => i + 1);
  }

  const pages: (number | string)[] = [];
  pages.push(1);

  if (current > 4) pages.push("...");

  const windowStart = Math.max(2, current - 1);
  const windowEnd = Math.min(total - 1, current + 1);
  for (let i = windowStart; i <= windowEnd; i++) pages.push(i);

  if (current < total - 3) pages.push("...");

  pages.push(total);
  return pages;
});

// Sync backend pagination into tablePagination, but DO NOT overwrite UI per_page except if UI didn't set it.
watch(
  () => getData.pagination.value,
  (newVal) => {
    if (!newVal) return;

    tablePagination.value.current_page = newVal.current_page || 1;
    tablePagination.value.total_pages = newVal.total_pages || 1;
    tablePagination.value.total = newVal.total || 0;

    // keep UI rows per page unless backend changed
    tablePagination.value.per_page = tablePagination.value.per_page;
    tablePagination.value.has_prev = tablePagination.value.current_page > 1;
    tablePagination.value.has_next =
      tablePagination.value.current_page < tablePagination.value.total_pages;
  },
  { immediate: true, deep: true }
);
// sync reactive sources
watch(getData.data, (newVal) => {
  rows.value = newVal || [];
});

// === ACTIONS ===
const changeRows = async () => {
  // when user changes rows, reset to page 1 and fetch
  currentPage.value = 1;

  await getData.fetchData({ page: 1, limit: tablePagination.value.per_page }); 
};

const handlePageChange = async (page: number) => {
  // protect bounds
  if (page < 1 || page === tablePagination.value.current_page) return;
  if (
    tablePagination.value.total_pages &&
    page > tablePagination.value.total_pages
  )
    return;

  currentPage.value = page;
  await getData.fetchData({ page, limit: tablePagination.value.per_page });
};
 

// --- Table ---
const tableConfig = computed(() => ({
  columns: [
    { name: "Icon", field: "icon", sortable: false },
    { name: "Title", field: "title", sortable: true },
  ],
  data: rows.value,
  actions: activeTab.value === "default" ? [true] : [false], // Only show actions for default tab
  loading: isLoading.value,
}));

// --- Modals ---
const isCreateModalOpen = ref(false);
const isEditModalOpen = ref(false);
const isViewMode = ref(false);

// --- Form Data ---
const newItemData = ref({ title: "", icon: null });
const editingItemData = ref({ _id: "", title: "", icon: null });

// --- Form Fields (without icon) ---
const createFormFields = [
  {
    key: "title",
    label: "Title",
    type: "text" as const,
    required: true,
    placeholder: "Enter title",
  },
];
const editFormFields = computed(() => [
  {
    key: "title",
    label: "Title",
    type: "text" as const,
    required: true,
    placeholder: "Enter title",
    readonly: isViewMode.value,
  },
]);

// --- Validation ---
const isCreateFormValid = computed(() => !newItemData.value.title.trim());
const isEditFormValid = computed(() => !editingItemData.value.title?.trim());

// Tab methods
const setActiveTab = (tab: "default" | "byusers") => {
  activeTab.value = tab;
  getData.fetchData({
    page: 1,
    limit: tablePagination.value.per_page,
    default: tab === "default",
  });
};

const openCreateModal = () => {
  isCreateModalOpen.value = true;
};

// --- Handlers ---
const handleEdit = (row: any) => {
  isViewMode.value = false;
  editingItemData.value = { ...row };
  isEditModalOpen.value = true;
};
const handleView = (row: any) => {
  isViewMode.value = true;
  editingItemData.value = { ...row };
  isEditModalOpen.value = true;
};
const handleDelete = async (id: string) => {
  if (confirm("Are you sure?")) {
    await crud.deleteItem(id);
    await refreshTable();
  }
};

const handleSaveCreate = async () => {
  if (isCreateFormValid.value) return;
  const payload = { ...newItemData.value };
  const result = await crud.createItem(payload);
  if (result) {
    closeCreateModal();
    await refreshTable();
  }
};

const handleSaveEdit = async () => {
  const id = editingItemData.value._id;
  if (!id) return;
  const { _id, ...updatePayload } = editingItemData.value;
  const result = await crud.updateItem(id, updatePayload);
  if (result) {
    closeEditModal();
    await refreshTable();
  }
};
const refreshTable = async () => {
  await getData.fetchData({
    page: tablePagination.value.current_page,
    limit: tablePagination.value.per_page,
    default: activeTab.value === "default",
  });
};


const closeCreateModal = () => {
  isCreateModalOpen.value = false;
  newItemData.value = { title: "", icon: null };
};
const closeEditModal = () => {
  isEditModalOpen.value = false;
  isViewMode.value = false;
  editingItemData.value = { _id: "", title: "", icon: null };
};


onMounted(async () => {
  getData.fetchData({
    page: tablePagination.value.current_page,
    limit: tablePagination.value.per_page,
    default: activeTab.value === "default",
  });
});
</script>

<style scoped>
.nav-tabs .nav-link {
  border-bottom: 2px solid transparent;
  color: #6c757d;
  font-weight: 500;
}

.nav-tabs .nav-link.active {
  background-color: transparent;
  border-color: transparent transparent #35bfbf transparent;
  color: #35bfbf;
  font-weight: 700;
}

.nav-tabs .nav-link:hover {
  border-color: transparent transparent #dee2e6 transparent;
  color: #495057;
}

/* pagination style */
.rows-select {
  width: 82px;
  padding: 8px 17px;
  font-size: 0.85rem;
  background-color: transparent;
}

/* Mini Pagination */
.pagination-mini {
  display: flex;
  align-items: center;
  border-radius: 6px;
  overflow: hidden;
}

.page-btn {
  border: none;
  background: transparent;
  font-size: 1rem;
  padding: 7px 12px;
  cursor: pointer;
  color: #6c757d;
  border-radius: 0px;
  transition: 0.2s;
  border: 1px solid #dee2e6;
  outline: none;
}
.page-btn:first-child {
  border-top-left-radius: 6px;
  border-bottom-left-radius: 6px;
}
.page-btn:last-child {
  border-top-right-radius: 6px;
  border-bottom-right-radius: 6px;
}
.page-btn:hover {
  background: #e9ecef;
}

.page-btn:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}

/* Active page (highlight like the screenshot) */
.page-active {
  background: #21c1d6;
  color: #fff;
  padding: 7px 12px;
  font-weight: 600;
  min-width: 28px;
  text-align: center;
  display: inline-block;
  border: 1px solid #21c1d6;
}
.page-active:hover {
  background: #21c1d6;
}
.prev_btn,
.next_btn {
  color: #21c1d6;
}
</style>
